import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, Observable, catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import { BRANDING_LOGO_TYPES, BrandingApiService } from '../../services/branding-api.service';
import { HeaderColorService } from '../../services/header-color.service';
import { DEFAULT_LOGO_PATH, HeaderLogoService } from '../../services/header-logo.service';
import { HeaderNameService } from '../../services/header-name.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { SectionTitleComponent } from '../section-title/section-title.component';
import { VerwaltungBereich } from '../verwaltung/verwaltung-bereich';
import { VerwaltungStore } from '../verwaltung/verwaltung.store';
import { VwUngespeichertComponent } from '../verwaltung/vw-ungespeichert.component';
import { FARB_VORSCHLAEGE } from './erscheinungsbild-farbvorschlaege';

export const ANWENDUNGSNAME_MAX = 40;

const HEX_FARBE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const HEX_KURZ = /^#([0-9A-Fa-f])([0-9A-Fa-f])([0-9A-Fa-f])$/;
const LOGO_MAX_BYTES = 5 * 1024 * 1024;
const LOGO_FORMAT_FEHLER = 'erscheinungsbild.logoFormatError';
const LOGO_GROESSE_FEHLER = 'erscheinungsbild.logoTooLargeError';

/** Das Backend nimmt nur sechsstellige Werte an. */
export function erweitereHex(wert: string): string {
  return wert.replace(HEX_KURZ, (_, r: string, g: string, b: string) => `#${r}${r}${g}${g}${b}${b}`).toUpperCase();
}

@Component({
  selector: 'app-erscheinungsbild',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatButton,
    MatIcon,
    SectionTitleComponent,
    ConfirmDialogComponent,
    VwUngespeichertComponent,
  ],
  templateUrl: './erscheinungsbild.component.html',
  styleUrl: './erscheinungsbild.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErscheinungsbildComponent implements VerwaltungBereich {
  private readonly brandingApi = inject(BrandingApiService);
  private readonly headerFarbe = inject(HeaderColorService);
  private readonly headerLogo = inject(HeaderLogoService);
  private readonly headerName = inject(HeaderNameService);
  private readonly store = inject(VerwaltungStore);

  readonly nameMax = ANWENDUNGSNAME_MAX;
  readonly farbVorschlaege = FARB_VORSCHLAEGE;
  readonly logoAccept = BRANDING_LOGO_TYPES.join(',');

  private readonly gespeicherteFarbe = signal(this.headerFarbe.color());
  private readonly gespeicherterName = signal(this.headerName.name());
  private readonly logoLoeschen = signal(false);
  private readonly neuesLogo = signal<{ datei: File; vorschauUrl: string } | null>(null);
  readonly logoFehler = signal<string | null>(null);
  readonly loeschenFragen = signal(false);

  readonly form = inject(NonNullableFormBuilder).group({
    color: [this.gespeicherteFarbe(), [Validators.required, Validators.pattern(HEX_FARBE)]],
    name: [this.gespeicherterName(), [Validators.required, Validators.maxLength(ANWENDUNGSNAME_MAX)]],
  });

  private readonly formTakt = toSignal(this.form.events.pipe(map((_, i) => i)), { initialValue: -1 });
  readonly werte = computed(() => {
    this.formTakt();
    return this.form.getRawValue();
  });

  readonly farbFehler = computed(() => {
    this.formTakt();
    const feld = this.form.controls.color;
    if (!feld.dirty && !feld.touched) return null;
    if (feld.hasError('required')) return 'erscheinungsbild.colorRequiredError';
    return feld.hasError('pattern') ? 'erscheinungsbild.colorInvalidError' : null;
  });

  /** Ein zu langer Bestandsname wird schon beim Öffnen gemeldet; tippen lässt sich darüber wegen maxlength nicht. */
  readonly nameFehler = computed(() => {
    this.formTakt();
    const feld = this.form.controls.name;
    if (feld.hasError('maxlength')) return 'erscheinungsbild.nameMaxLengthError';
    return (feld.dirty || feld.touched) && feld.hasError('required') ? 'erscheinungsbild.nameRequiredError' : null;
  });

  readonly vorschauFarbe = computed(() => {
    const farbe = this.werte().color;
    return HEX_FARBE.test(farbe) ? erweitereHex(farbe) : this.gespeicherteFarbe();
  });

  readonly vorschauLogo = computed(() => {
    if (this.logoLoeschen()) return DEFAULT_LOGO_PATH;
    return this.neuesLogo()?.vorschauUrl ?? this.headerLogo.logoSrc();
  });

  readonly hatUngespeicherteAenderungen = computed(() => {
    const { color, name } = this.werte();
    return (
      color !== this.gespeicherteFarbe() ||
      name !== this.gespeicherterName() ||
      this.logoLoeschen() ||
      this.neuesLogo() !== null
    );
  });

  readonly kannSpeichern = computed(() => {
    this.formTakt();
    return this.form.valid;
  });

  constructor() {
    this.form.controls.color.valueChanges.pipe(takeUntilDestroyed()).subscribe((wert) => {
      if (wert !== wert.toUpperCase()) this.form.controls.color.setValue(wert.toUpperCase());
    });
    // Nach einem Neuladen kann das Branding erst nach dem Aufbau eintreffen; ohne Nachziehen ginge ein Standardwert ans Backend.
    effect(() => {
      const farbe = this.headerFarbe.color().toUpperCase();
      const name = this.headerName.name();
      untracked(() => {
        this.ausgangswertNachziehen(this.form.controls.color, this.gespeicherteFarbe, farbe);
        this.ausgangswertNachziehen(this.form.controls.name, this.gespeicherterName, name);
      });
    });
    this.store.bereichAnmelden(this);
    inject(DestroyRef).onDestroy(() => {
      this.store.bereichAbmelden(this);
      this.neuesLogoVerwerfen();
    });
  }

  farbeWaehlen(farbe: string): void {
    this.form.controls.color.setValue(farbe.toUpperCase());
    this.form.controls.color.markAsTouched();
  }

  farbwaehlerGeaendert(event: Event): void {
    this.farbeWaehlen((event.target as HTMLInputElement).value);
  }

  hexErweitern(): void {
    const feld = this.form.controls.color;
    const erweitert = erweitereHex(feld.value);
    if (erweitert !== feld.value) feld.setValue(erweitert);
  }

  logoGewaehlt(event: Event): void {
    const eingabe = event.target as HTMLInputElement;
    const datei = eingabe.files?.[0];
    eingabe.value = '';
    if (!datei) return;
    this.neuesLogoVerwerfen();
    this.logoFehler.set(null);
    if (!BRANDING_LOGO_TYPES.includes(datei.type)) {
      this.logoFehler.set(LOGO_FORMAT_FEHLER);
      return;
    }
    if (datei.size > LOGO_MAX_BYTES) {
      this.logoFehler.set(LOGO_GROESSE_FEHLER);
      return;
    }
    this.logoLoeschen.set(false);
    this.neuesLogo.set({ datei, vorschauUrl: URL.createObjectURL(datei) });
  }

  loeschenBestaetigen(): void {
    this.neuesLogoVerwerfen();
    this.logoFehler.set(null);
    this.logoLoeschen.set(true);
    this.loeschenFragen.set(false);
  }

  speichern(): Observable<void> {
    this.hexErweitern();
    this.form.markAllAsTouched();
    if (this.form.invalid) return EMPTY;
    const { color, name } = this.form.getRawValue();
    const logo = this.neuesLogo();
    const loeschen = this.logoLoeschen();

    return (logo ? this.logoHochladen(logo.datei) : of(false)).pipe(
      switchMap((hochgeladen) =>
        this.brandingApi
          // Den Logo-Pfad setzt der Upload; nur Löschen schickt das Feld, sonst bliebe ein veralteter Client-Stand.
          .updateBranding({ name, headerColor: color, logoPath: loeschen ? null : undefined })
          .pipe(map((daten) => ({ daten, hochgeladen }))),
      ),
      map(({ daten, hochgeladen }) => {
        this.headerFarbe.loadFromBackend(daten.headerColor);
        this.headerName.loadFromBackend(daten.name);
        if (!hochgeladen) this.headerLogo.loadFromBackend(daten.logoPath);
        this.gespeicherteFarbe.set(color);
        this.gespeicherterName.set(name);
        this.logoLoeschen.set(false);
        this.form.markAsPristine();
        this.form.markAsUntouched();
      }),
    );
  }

  verwerfen(): void {
    this.form.setValue({ color: this.gespeicherteFarbe(), name: this.gespeicherterName() });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.logoLoeschen.set(false);
    this.neuesLogoVerwerfen();
    this.logoFehler.set(null);
  }

  /** Ein abgelehntes Bild (400/413) beendet das Speichern ohne Wert, die Meldung steht am Logo statt in der Snackbar. */
  private logoHochladen(datei: File): Observable<boolean> {
    return this.brandingApi.uploadLogo(datei).pipe(
      tap((daten) => {
        this.headerLogo.loadFromBackend(daten.logoPath);
        this.neuesLogoVerwerfen();
      }),
      map(() => true),
      catchError((err: unknown) => {
        const status = err instanceof HttpErrorResponse ? err.status : undefined;
        if (status !== 400 && status !== 413) return throwError(() => err);
        this.neuesLogoVerwerfen();
        this.logoFehler.set(status === 413 ? LOGO_GROESSE_FEHLER : LOGO_FORMAT_FEHLER);
        return EMPTY;
      }),
    );
  }

  /** Eine begonnene Eingabe bleibt stehen; der neue Wert gilt trotzdem als gespeicherter Stand. */
  private ausgangswertNachziehen(feld: FormControl<string>, gespeichert: WritableSignal<string>, wert: string): void {
    if (wert === gespeichert()) return;
    const unberuehrt = feld.pristine && feld.value === gespeichert();
    gespeichert.set(wert);
    if (unberuehrt) feld.setValue(wert);
  }

  private neuesLogoVerwerfen(): void {
    const logo = this.neuesLogo();
    if (logo) URL.revokeObjectURL(logo.vorschauUrl);
    this.neuesLogo.set(null);
  }
}
